import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, UNAUTHENTICATED_EVENT, errorMessage } from '../api/client';
import { authApi } from '../api/endpoints';
import type { SessionUser } from '../api/types';

type Status = 'chargement' | 'connecte' | 'anonyme';

type SessionValue = {
  status: Status;
  user: SessionUser | null;
  /** Erreur de chargement de session, hors simple absence de session. */
  loadError: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('chargement');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    authApi
      .session(controller.signal)
      .then(({ user: loaded }) => {
        if (!active) return;
        setUser(loaded);
        setStatus('connecte');
      })
      .catch((cause: unknown) => {
        if (!active || controller.signal.aborted) return;
        setUser(null);
        setStatus('anonyme');
        // un 401 est le cas normal « pas encore connecté » ; le reste est un
        // incident et doit se voir
        if (!(cause instanceof ApiError) || cause.status !== 401) {
          setLoadError(errorMessage(cause));
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const onLost = (): void => {
      setUser(null);
      setStatus('anonyme');
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, onLost);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onLost);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: connected } = await authApi.login(email, password);
    setLoadError(null);
    setUser(connected);
    setStatus('connecte');
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setStatus('anonyme');
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ status, user, loadError, login, logout }),
    [status, user, loadError, login, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession doit être utilisé dans un SessionProvider.');
  }
  return value;
}

/** Utilisateur garanti présent : à n'appeler que sous une route protégée. */
export function useCurrentUser(): SessionUser {
  const { user } = useSession();
  if (user === null) {
    throw new Error('Aucun utilisateur en session sur une route protégée.');
  }
  return user;
}
